// src/components/PaywallModal.jsx
/**
 * Full-screen paywall modal.
 * Shows contextual messaging based on which feature was blocked,
 * then lets the user pick a plan and redirects to Stripe Checkout.
 */
import { useState } from 'react';
import { usePremium } from '../hooks/usePremium';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// ─── Feature copy map ─────────────────────────────────────────────────────────
const FEATURE_COPY = {
  direct_messages: {
    icon:    '✉️',
    title:   'You\'ve reached your message limit',
    body:    'Free accounts can start up to 5 new conversations per month. Upgrade to send unlimited direct messages to any vendor or organization on the platform.',
    badge:   'Messaging',
  },
  bid_submitted: {
    icon:    '⚡',
    title:   'Monthly bid limit reached',
    body:    'Free vendor accounts can submit 5 bids per month. Premium vendors get unlimited bids — delivered directly to any problem posted on the platform.',
    badge:   'Bidding',
  },
  solution_view_history: {
    icon:    '📊',
    title:   'Unlock viewer analytics',
    body:    'See exactly who viewed your solutions over the last 30 days — company names, roles, and timestamps. Available on Vendor Premium.',
    badge:   'Analytics',
  },
  cross_sector: {
    icon:    '🌐',
    title:   'Discover cross-sector solutions',
    body:    'Your free plan shows solutions in your industry only. Premium unlocks the full marketplace — find innovation happening in every sector.',
    badge:   'Discovery',
  },
  competitor_insights: {
    icon:    '📈',
    title:   'See how you rank',
    body:    'Get benchmarks on how your solutions compare to competitors on every tagged problem. Know where you lead and where to improve.',
    badge:   'Insights',
  },
  tagged_solutions: {
    icon:    '💡',
    title:   'See all matched solutions',
    body:    'Free accounts see up to 5 tagged solutions per problem. Upgrade to unlock every match — plus AI-powered best-match recommendations.',
    badge:   'Solutions',
  },
  best_match_insight: {
    icon:    '✦',
    title:   'Unlock AI best-match insights',
    body:    'Our AI ranks all tagged solutions against your specific problem criteria and surfaces the best fit. Available on Organization Premium.',
    badge:   'AI Insights',
  },
  default: {
    icon:    '🔒',
    title:   'This is a Premium feature',
    body:    'Upgrade your account to unlock this feature and get the most out of the NEXUS ecosystem.',
    badge:   'Premium',
  },
};

// ─── Plan cards data ──────────────────────────────────────────────────────────
const PLAN_CARDS = [
  {
    key:       'vendor_premium',
    name:      'Vendor Premium',
    price:     '$49',
    interval:  '/month',
    tagline:   'For suppliers & solution providers',
    color:     '#4f8eff',
    features:  [
      'Unlimited direct messages',
      'Submit bids to any problem',
      '30-day solution viewer history',
      'Cross-sector solution discovery',
      'Competitor insight benchmarks',
    ],
  },
  {
    key:       'organization_premium',
    name:      'Organization Premium',
    price:     '$79',
    interval:  '/month',
    tagline:   'For organizations posting problems',
    color:     '#00e5b0',
    features:  [
      'Unlimited direct messages',
      'See all tagged solutions per problem',
      'AI best-match solution insight',
      'Cross-sector solution discovery',
    ],
  },
];

// ─── Component ────────────────────────────────────────────────────────────────
export default function PaywallModal() {
  const { modalOpen, modalFeature, closeUpgradeModal } = usePremium();
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [error, setError]            = useState('');

  if (!modalOpen) return null;

  const copy = FEATURE_COPY[modalFeature] || FEATURE_COPY.default;

  const handleUpgrade = async (planType) => {
    setLoadingPlan(planType);
    setError('');
    try {
      // Fetch plans, find the matching one, then create checkout
      const { data: plans } = await axios.get(`${API}/billing/plans/`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
      });
      const plan = plans.find(p => p.plan_type === planType);
      if (!plan) { setError('Plan not found. Please try again.'); return; }

      const { data } = await axios.post(
        `${API}/billing/checkout/`,
        { plan_id: plan.id },
        { headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } }
      );
      window.location.href = data.checkout_url;
    } catch (err) {
      setError(err?.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setLoadingPlan(null);
    }
  };

  return (
    <div style={styles.overlay} onClick={e => e.target === e.currentTarget && closeUpgradeModal()}>
      <div style={styles.modal}>

        {/* Close */}
        <button style={styles.close} onClick={closeUpgradeModal}>✕</button>

        {/* Feature block */}
        <div style={styles.featureBlock}>
          <span style={styles.featureIcon}>{copy.icon}</span>
          <div>
            <div style={styles.badge}>{copy.badge}</div>
            <h2 style={styles.title}>{copy.title}</h2>
            <p style={styles.body}>{copy.body}</p>
          </div>
        </div>

        <div style={styles.divider} />

        {/* Plan cards */}
        <p style={styles.chooseLabel}>Choose your plan</p>
        <div style={styles.plansRow}>
          {PLAN_CARDS.map(plan => (
            <div key={plan.key} style={{ ...styles.planCard, borderColor: plan.color }}>
              <div style={{ ...styles.planAccent, background: plan.color }} />
              <div style={styles.planName}>{plan.name}</div>
              <div style={styles.planTagline}>{plan.tagline}</div>
              <div style={styles.planPrice}>
                <span style={{ ...styles.planAmount, color: plan.color }}>{plan.price}</span>
                <span style={styles.planInterval}>{plan.interval}</span>
              </div>
              <ul style={styles.featureList}>
                {plan.features.map(f => (
                  <li key={f} style={styles.featureItem}>
                    <span style={{ ...styles.check, color: plan.color }}>✓</span> {f}
                  </li>
                ))}
              </ul>
              <button
                style={{ ...styles.upgradeBtn, background: plan.color }}
                onClick={() => handleUpgrade(plan.key)}
                disabled={!!loadingPlan}
              >
                {loadingPlan === plan.key ? 'Redirecting…' : 'Upgrade Now →'}
              </button>
            </div>
          ))}
        </div>

        {error && <p style={styles.error}>{error}</p>}

        <p style={styles.footer}>
          No commitment — cancel anytime · Billed securely via Stripe
        </p>
      </div>
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const styles = {
  overlay: {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,.75)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 2000, padding: '1rem',
  },
  modal: {
    background: '#0f1117',
    border: '1px solid #2a3347',
    borderRadius: 20,
    padding: '2rem',
    width: '100%',
    maxWidth: 680,
    maxHeight: '92vh',
    overflowY: 'auto',
    position: 'relative',
    fontFamily: "'Instrument Sans', sans-serif",
    color: '#e8eaf2',
  },
  close: {
    position: 'absolute', top: '1rem', right: '1rem',
    background: 'none', border: 'none', color: '#606880',
    fontSize: '1rem', cursor: 'pointer', padding: '.35rem .55rem',
    borderRadius: 6,
  },
  featureBlock: {
    display: 'flex', gap: '1rem', alignItems: 'flex-start',
    marginBottom: '1.25rem',
  },
  featureIcon: { fontSize: '2rem', flexShrink: 0 },
  badge: {
    display: 'inline-block',
    background: 'rgba(79,142,255,.15)', color: '#7aacff',
    fontSize: '.7rem', fontWeight: 600, letterSpacing: '.06em',
    textTransform: 'uppercase', padding: '.2rem .55rem',
    borderRadius: 20, marginBottom: '.4rem',
  },
  title: {
    fontFamily: "'Bricolage Grotesque', sans-serif",
    fontSize: '1.15rem', fontWeight: 700, margin: '0 0 .4rem',
    color: '#e8eaf2',
  },
  body: { fontSize: '.88rem', color: '#8892a4', lineHeight: 1.6, margin: 0 },
  divider: { height: 1, background: '#1e2432', margin: '1.25rem 0' },
  chooseLabel: {
    fontSize: '.75rem', fontWeight: 600, letterSpacing: '.08em',
    textTransform: 'uppercase', color: '#606880', marginBottom: '1rem',
  },
  plansRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
    gap: '1rem',
  },
  planCard: {
    background: '#151922', border: '2px solid',
    borderRadius: 14, padding: '1.25rem',
    display: 'flex', flexDirection: 'column', gap: '.35rem',
    position: 'relative', overflow: 'hidden',
  },
  planAccent: {
    position: 'absolute', top: 0, left: 0, right: 0, height: 3,
  },
  planName: {
    fontFamily: "'Bricolage Grotesque', sans-serif",
    fontWeight: 700, fontSize: '.95rem', marginTop: '.25rem',
  },
  planTagline: { fontSize: '.75rem', color: '#8892a4', marginBottom: '.5rem' },
  planPrice: { display: 'flex', alignItems: 'baseline', gap: '.3rem', marginBottom: '.5rem' },
  planAmount: { fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: '1.6rem', fontWeight: 700 },
  planInterval: { fontSize: '.8rem', color: '#606880' },
  featureList: { listStyle: 'none', padding: 0, margin: '0 0 1rem', display: 'flex', flexDirection: 'column', gap: '.3rem' },
  featureItem: { fontSize: '.82rem', color: '#8892a4', display: 'flex', gap: '.5rem', alignItems: 'flex-start' },
  check: { fontWeight: 700, flexShrink: 0, marginTop: 1 },
  upgradeBtn: {
    border: 'none', borderRadius: 9, padding: '.65rem',
    fontFamily: "'Instrument Sans', sans-serif",
    fontWeight: 600, fontSize: '.88rem', color: '#000',
    cursor: 'pointer', marginTop: 'auto',
    transition: 'opacity .15s',
  },
  error: { color: '#f87171', fontSize: '.82rem', textAlign: 'center', marginTop: '.75rem' },
  footer: { fontSize: '.72rem', color: '#606880', textAlign: 'center', marginTop: '1rem' },
};
